import {
    GET_CHATS_SUCCESS,
    GET_CHAT_USERS_SUCCESS,
    CREATE_CHAT_SUCCESS,
    GET_MESSAGES_SUCCESS,
    SEND_MESSAGE_SUCCESS,
    SEARCH_MESSAGES_SUCCESS,
    SET_ACTIVE_CHAT,
    CLEAR_ACTIVE_CHAT,
    CLEAR_MESSAGES,
    CLEAR_CHAT_USERS,
    CHAT_DELETED_EVENT,
    CHAT_MEMBER_REMOVED_EVENT,
    REMOVE_CHAT_MEMBER_SUCCESS,
    ADD_CHAT_MEMBERS_SUCCESS,
    CHAT_MEMBERS_ADDED_EVENT,
    MUTE_CHAT_SUCCESS,
    CHAT_RECEIVE_MESSAGE,
    CHAT_UNREAD_UPDATE,
    CHAT_MESSAGE_EDITED,
    CHAT_MESSAGE_DELETED,
    CHAT_MESSAGE_READ,
    CHAT_SET_TYPING,
    CHAT_CLEAR_TYPING,
} from './chat.types';

const initialState = {
    chats: [],
    chatUsers: [],
    activeChat: null,
    messages: [],
    messageCount: 0,
    searchResults: null,
    typingUsers: {},
};

export const chatReducer = (state = initialState, action) => {
    switch (action.type) {
        case GET_CHATS_SUCCESS:
            return {
                ...state,
                chats: action.payload?.chats || action.payload || [],
            };

        case GET_CHAT_USERS_SUCCESS:
            return {
                ...state,
                chatUsers: action.payload || [],
            };

        case CREATE_CHAT_SUCCESS: {
            const newChatId = action.payload?.chatId;
            const existsInList = state.chats.some((c) => c.chatId === newChatId);
            return {
                ...state,
                // If chat already exists (backend returned existing), update it in place; otherwise prepend
                chats: existsInList
                    ? state.chats.map((c) => (c.chatId === newChatId ? action.payload : c))
                    : [action.payload, ...state.chats],
                activeChat: action.payload,
            };
        }

        // Received via socket when another user deletes a chat
        case CHAT_DELETED_EVENT: {
            const deletedChatId = action.payload?.chatId;
            const isActiveDeleted = state.activeChat?.chatId === deletedChatId;
            return {
                ...state,
                chats: state.chats.filter((c) => c.chatId !== deletedChatId),
                activeChat: isActiveDeleted ? null : state.activeChat,
                messages: isActiveDeleted ? [] : state.messages,
                messageCount: isActiveDeleted ? 0 : state.messageCount,
            };
        }

        // REST response to whoever added/removed members, and socket chat:members-added /
        // chat:member-removed to everyone else in the chat. All carry the new member list. The
        // "<name> was added/removed" message itself arrives like any other message (dedup by id).
        case REMOVE_CHAT_MEMBER_SUCCESS:
        case ADD_CHAT_MEMBERS_SUCCESS:
        case CHAT_MEMBERS_ADDED_EVENT:
        case CHAT_MEMBER_REMOVED_EVENT: {
            const { chatId, participants, message } = action.payload || {};
            const isActiveChat = state.activeChat?.chatId === chatId;
            const addMessage = isActiveChat && message && !state.messages.some((m) => m.id === message.id);
            return {
                ...state,
                chats: state.chats.map((c) => (c.chatId === chatId ? { ...c, participants } : c)),
                activeChat: isActiveChat ? { ...state.activeChat, participants } : state.activeChat,
                messages: addMessage ? [message, ...state.messages] : state.messages,
                messageCount: addMessage ? state.messageCount + 1 : state.messageCount,
            };
        }

        case MUTE_CHAT_SUCCESS: {
            const { chatId, muted } = action.payload;
            return {
                ...state,
                chats: state.chats.map((c) =>
                    c.chatId === chatId ? { ...c, participants: c.participants?.map((p) => ({ ...p, hiddenNotification: muted })) } : c
                ),
                activeChat:
                    state.activeChat?.chatId === chatId
                        ? {
                              ...state.activeChat,
                              participants: state.activeChat.participants?.map((p) => ({
                                  ...p,
                                  hiddenNotification: muted,
                              })),
                          }
                        : state.activeChat,
            };
        }

        case GET_MESSAGES_SUCCESS:
            return {
                ...state,
                messages:
                    action.payload?.skip > 0 ? [...state.messages, ...(action.payload?.messages || [])] : action.payload?.messages || [],
                messageCount: action.payload?.count || 0,
            };

        case SEND_MESSAGE_SUCCESS: {
            const newMsg = action.payload;
            const alreadyExists = state.messages.some((m) => m.id === newMsg?.id);
            // Update the chats list with the new last message and re-sort
            const updatedChatsAfterSend = state.chats.map((c) =>
                c.chatId === newMsg?.chatId
                    ? {
                          ...c,
                          lastMessage: {
                              text: newMsg.text || (newMsg.attachments?.length ? 'Attachment' : ''),
                              createdAt: newMsg.createdAt,
                              sender: newMsg.sender,
                          },
                          lastDate: newMsg.createdAt || c.lastDate,
                      }
                    : c
            );
            const sortedChatsAfterSend = [...updatedChatsAfterSend].sort((a, b) => new Date(b.lastDate || 0) - new Date(a.lastDate || 0));
            return {
                ...state,
                messages: alreadyExists ? state.messages : [newMsg, ...state.messages],
                chats: sortedChatsAfterSend,
            };
        }

        case SEARCH_MESSAGES_SUCCESS:
            return {
                ...state,
                searchResults: action.payload,
            };

        case SET_ACTIVE_CHAT:
            return {
                ...state,
                activeChat: action.payload,
            };

        case CLEAR_ACTIVE_CHAT:
            return {
                ...state,
                activeChat: null,
                messages: [],
                messageCount: 0,
                searchResults: null,
            };

        case CLEAR_MESSAGES:
            return {
                ...state,
                messages: [],
                messageCount: 0,
            };

        case CLEAR_CHAT_USERS:
            return {
                ...state,
                chatUsers: [],
            };

        // Received from chat room via chat:new-message — full message payload
        // Only fires when user has joined the chat room (chat is open)
        case CHAT_RECEIVE_MESSAGE: {
            const { chatId, message } = action.payload;
            const isActiveChat = state.activeChat?.chatId === chatId;
            // Dedup: sender already has the message from SEND_MESSAGE_SUCCESS
            const alreadyExists = state.messages.some((m) => m.id === message?.id);

            // Enrich sender.user from participants if missing (fixes "undefined" name)
            let enrichedMessage = message;
            if (message && !message.sender?.user?.firstName && state.activeChat?.participants) {
                const participant = state.activeChat.participants.find((p) => p.userId === message.sender?.userId);
                if (participant?.user) {
                    enrichedMessage = {
                        ...message,
                        sender: {
                            ...message.sender,
                            user: { ...(message.sender?.user || {}), ...participant.user },
                        },
                    };
                }
            }

            // Update the chats list with the new last message
            const chatsAfterReceive = state.chats.map((c) =>
                c.chatId === chatId
                    ? {
                          ...c,
                          lastMessage: {
                              text: enrichedMessage?.text || (enrichedMessage?.attachments?.length ? 'Attachment' : ''),
                              type: enrichedMessage?.type,
                              createdAt: enrichedMessage?.createdAt,
                              sender: enrichedMessage?.sender,
                          },
                          lastDate: enrichedMessage?.createdAt || c.lastDate,
                      }
                    : c
            );
            const sortedChatsAfterReceive = [...chatsAfterReceive].sort((a, b) => new Date(b.lastDate || 0) - new Date(a.lastDate || 0));

            return {
                ...state,
                messages: isActiveChat && !alreadyExists ? [enrichedMessage, ...state.messages] : state.messages,
                messageCount: isActiveChat && !alreadyExists ? state.messageCount + 1 : state.messageCount,
                chats: sortedChatsAfterReceive,
            };
        }

        // Received from user room via chat:unread-update — lightweight payload
        // Always fires for all participants (user room is always joined)
        case CHAT_UNREAD_UPDATE: {
            const { chatId, lastMessage, unreadCount } = action.payload;
            const isActiveChat = state.activeChat?.chatId === chatId;
            const chatExists = state.chats.some((c) => c.chatId === chatId);
            if (!chatExists) return state;
            const updatedChats = state.chats.map((c) =>
                c.chatId === chatId
                    ? {
                          ...c,
                          lastMessage: {
                              ...(c.lastMessage || {}),
                              // The server sends the whole message (textPreview was dropped in Feb 2026)
                              text:
                                  lastMessage?.textPreview ||
                                  lastMessage?.text ||
                                  (lastMessage?.attachments?.length ? 'Attachment' : c.lastMessage?.text),
                              type: lastMessage ? lastMessage.type : c.lastMessage?.type,
                              createdAt: lastMessage?.createdAt || c.lastMessage?.createdAt,
                              sender: lastMessage?.sender || c.lastMessage?.sender,
                          },
                          lastDate: lastMessage?.createdAt || c.lastDate,
                          // If user is viewing this chat, keep unread at 0
                          unread: isActiveChat ? 0 : unreadCount,
                      }
                    : c
            );
            const sortedChats = [...updatedChats].sort((a, b) => new Date(b.lastDate || 0) - new Date(a.lastDate || 0));
            return {
                ...state,
                chats: sortedChats,
            };
        }

        // Received from chat:message-edited — payload: { chatId, message }
        case CHAT_MESSAGE_EDITED: {
            const edited = action.payload?.message || action.payload;
            return {
                ...state,
                messages: state.messages.map((m) => (m.id === edited.id ? { ...m, text: edited.text, editedAt: edited.editedAt } : m)),
            };
        }

        // Received from chat:message-deleted — payload: { chatId, messageId, userId, userType }
        case CHAT_MESSAGE_DELETED: {
            const deletedId = action.payload?.messageId || action.payload?.id;
            return {
                ...state,
                messages: state.messages.filter((m) => m.id !== deletedId),
                messageCount: state.messageCount > 0 ? state.messageCount - 1 : 0,
            };
        }

        // Received from chat:chat-read or chat:message-read
        // payload.userId = who did the reading, payload.currentUserId = this browser's user
        case CHAT_MESSAGE_READ: {
            const { chatId, messageId, userId, userType, readAt, currentUserId } = action.payload;
            // Only clear the unread badge if the CURRENT user is the one who read.
            // When someone else reads our messages, it's a read receipt — not our unread clearing.
            const isCurrentUser = userId === currentUserId;
            return {
                ...state,
                chats: isCurrentUser ? state.chats.map((c) => (c.chatId === chatId ? { ...c, unread: 0 } : c)) : state.chats,
                // Update readBy on individual messages for the active chat (read receipts)
                messages: messageId
                    ? state.messages.map((m) =>
                          m.id === messageId
                              ? {
                                    ...m,
                                    readBy: [
                                        ...(m.readBy || []),
                                        ...(!(m.readBy || []).some((r) => r.userId === userId) ? [{ userId, userType, readAt }] : []),
                                    ],
                                }
                              : m
                      )
                    : // Chat-level read: mark all messages from the reader as read by them
                      state.messages.map((m) =>
                          m.sender?.userId !== userId && !(m.readBy || []).some((r) => r.userId === userId)
                              ? {
                                    ...m,
                                    readBy: [...(m.readBy || []), { userId, userType, readAt }],
                                }
                              : m
                      ),
            };
        }

        case CHAT_SET_TYPING: {
            const { chatId, userId, userType } = action.payload;
            const current = state.typingUsers[chatId] || [];
            const exists = current.find((u) => u.userId === userId && u.userType === userType);
            if (exists) return state;
            return {
                ...state,
                typingUsers: {
                    ...state.typingUsers,
                    [chatId]: [...current, { userId, userType }],
                },
            };
        }

        case CHAT_CLEAR_TYPING: {
            const { chatId, userId, userType } = action.payload;
            const currentTyping = state.typingUsers[chatId] || [];
            return {
                ...state,
                typingUsers: {
                    ...state.typingUsers,
                    [chatId]: currentTyping.filter((u) => !(u.userId === userId && u.userType === userType)),
                },
            };
        }

        default:
            return state;
    }
};
